import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { Loader2, XCircle, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * /auth/google — Google OAuth redirect handler.
 * Google redirects the browser here with ?code=...&state=...
 * We POST to backend /auth/google/exchange which sets the session cookie.
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function AuthGoogle() {
    const nav = useNavigate();
    const { refresh } = useAuth();
    const [status, setStatus] = useState("exchanging");
    const ran = useRef(false);

    useEffect(() => {
        if (ran.current) return;
        ran.current = true;
        const params = new URLSearchParams(window.location.search);
        const code = params.get("code");
        const state = params.get("state");
        const err = params.get("error");

        if (err) { setStatus("error"); return; }
        if (!code || !state) { setStatus("error"); return; }

        (async () => {
            try {
                await axios.post(
                    `${API}/auth/google/exchange`,
                    { code, state, redirect_uri: window.location.origin + "/auth/google" },
                    { withCredentials: true },
                );
                await refresh();
                setStatus("ok");
                setTimeout(() => nav("/dashboard", { replace: true }), 600);
            } catch {
                setStatus("error");
            }
        })();
    }, [nav, refresh]);

    return (
        <main className="flex min-h-screen items-center justify-center bg-limestone-100 pt-28" data-testid="auth-google-page">
            <div className="rounded-card border-2 border-ink-200 bg-white p-10 text-center max-w-md">
                {status === "exchanging" && (
                    <>
                        <Loader2 className="mx-auto h-10 w-10 animate-spin text-madder-500" />
                        <p className="mt-5 font-display text-[22px] font-bold text-ink-800">Signing you in…</p>
                        <p className="mt-2 text-[14px] font-medium text-ink-600">Completing Google sign-in.</p>
                    </>
                )}
                {status === "ok" && (
                    <>
                        <CheckCircle2 className="mx-auto h-10 w-10 text-redsea-500" />
                        <p className="mt-5 font-display text-[22px] font-bold text-ink-800">Signed in.</p>
                    </>
                )}
                {status === "error" && (
                    <>
                        <XCircle className="mx-auto h-10 w-10 text-madder-500" />
                        <p className="mt-5 font-display text-[22px] font-bold text-ink-800">Sign-in failed</p>
                        <p className="mt-2 text-[14px] font-medium text-ink-600">Please return to the sign-in page and try again.</p>
                        <button onClick={() => nav("/login")} className="mt-6 inline-flex h-[48px] items-center rounded-btn bg-ink-800 px-5 text-[14px] font-bold text-white">Back to sign-in</button>
                    </>
                )}
            </div>
        </main>
    );
}
